import React, { useState, useEffect, useContext, useCallback } from "react";
import { useHistory, useParams } from "react-router-dom";
import AlgoImg from "assets/images/access-algorithm.png";
import MultiFieldImg from "assets/images/multi-field.png";
import SaveEarthImg from "assets/images/save-earth.png";
import FarmManagementImg from "assets/images/farm-management.png";
import KondininSvg from "assets/images/svg/kondinin.svg";
import NorthQueenslandSvg from "assets/images/svg/north-queensland.svg";
import PearceyFundationSvg from "assets/images/svg/pearcey-foundation.svg";
import { Quotes, CheckSquare } from "phosphor-react";
import { UserContext } from "context/user";
import usePremium from "hooks/usePremium";
import "./index.scss";
import CustomModal from "components/Modal";
import { getAdvancedPricing } from "api/Stripe";
import { StripeActions, StripeContext } from "context/stripe";
import { getPricing } from "utils";
import ContentLoader from "components/ContentLoader";
import SubscriptionForm from "../components/SubscriptionForm";
import SubscriptionSuccess from "../components/SuccessSubscriptionPage";

const PremiumContainer = () => {
  const history = useHistory();
  const { state } = useParams();
  const [toggleModal, setToggleModal] = useState(false);
  const [offset, setOffset] = useState(0);
  const { token, profile } = useContext(UserContext);
  const { isPremium } = usePremium();
  const { state: stripeState, dispatch } = useContext(StripeContext);

  const { pricingDetails, isFetchingPricingDetails } = stripeState;
  useEffect(() => {
    if (["success", "subscribe"].includes(state)) {
      setToggleModal(true);
    }
  }, [state, isPremium]);

  useEffect(() => {
    window.onscroll = () => {
      setOffset(window.pageYOffset);
    };
  }, [profile]);

  const fetchPrice = useCallback(async () => {
    if (!token) return;
    dispatch({
      type: StripeActions.FETCH_PRICING_DETAILS,
    });
    try {
      const payload = await getAdvancedPricing(token);
      dispatch({
        type: StripeActions.FETCH_PRICING_DETAILS_SUCCESSFUL,
        value: payload,
      });
    } catch (error) {
      dispatch({
        type: StripeActions.FETCH_PRICING_DETAILS_FAIL,
      });
    }
  }, [dispatch, token]);

  useEffect(() => {
    fetchPrice();
  }, [fetchPrice]);

  const { actualPrice, basePrice, discountValue, isValidDiscount } =
    getPricing(pricingDetails);

  const renderPrice = () => {
    return <h3 className="price">${basePrice} AUD</h3>;
  };

  const renderDiscountedPrice = () => {
    return (
      <>
        <h3 className="price">${actualPrice} AUD</h3>
        {basePrice && <span className="append"> from ${basePrice}</span>}
        {discountValue && (
          <p className="discount">{`${discountValue}`} Discount offer</p>
        )}
      </>
    );
  };
  return (
    <>
      <div className="premium-container">
        <div className={`sticky-nav ${offset > 500 ? "-show" : "-hidden"}`}>
          <p className="text">Want more features?</p>
          <button
            className="button"
            type="button"
            onClick={() => setToggleModal(true)}
            disabled={isPremium}
          >
            {isPremium
              ? "You're already using advanced"
              : "Get started with Advanced"}
          </button>
        </div>
        <section className="hero-header">
          <h1 className="headline">Want more? Get Advanced</h1>
          <h5 className="paragraph">
            Advanced features enable more advanced crop and pasture management.
          </h5>
          <button
            className="button"
            type="button"
            onClick={() => setToggleModal(true)}
            disabled={isPremium}
          >
            {isPremium
              ? "You are subscribed to Advanced account"
              : "Get Started with Advanced"}
          </button>
        </section>
        <section className="features-section -multifield">
          <div className="details">
            <h3 className="title">Multiple field selection and viewing</h3>
            <p className="paragraph">
              Do you want to easily compare crop health across multiple fields?
              or the same crop type? Multiple Paddock Viewing allows you to do
              this, simply select the fields you want to view and the platform
              will do the rest!
            </p>
          </div>
          <img src={`${MultiFieldImg}`} alt="multifield img" />
        </section>
        <section className="features-section -algorithm">
          <img src={`${AlgoImg}`} alt="calculation img" />
          <div className="details">
            <h3 className="title">Access advanced calculations</h3>
            <p className="paragraph">
              NDVI is limited in some situations. Advanced calculations allow
              you to handle large bulky crop types, help with Nitrogen
              management, and detect water stresses early.
            </p>
          </div>
        </section>
        <section className="proof-section">
          <div className="proof-item">
            <Quotes size={40} color="#09ba51" weight="duotone" />
            <p className="quote">
              Some think of farm imagery as a nice shot of a beautiful sunset or
              sunrise. But if looking with a different set of eyes, the right
              type of imagery can reveal disease, stress or opportunity in crops
              and pastures.
            </p>
            <img src={`${KondininSvg}`} alt="kondinin img" className="image" />
          </div>
          <div className="proof-item">
            <Quotes size={40} color="#09ba51" weight="duotone" />
            <p className="quote">
              The team is developing products and services for farmers and
              agronomists that are simple, automated and low cost, and provide
              on-ground actionable solutions.
            </p>
            <img
              src={`${NorthQueenslandSvg}`}
              alt="nqueensland img"
              className="image"
            />
          </div>
          <div className="proof-item">
            <Quotes size={40} color="#09ba51" weight="duotone" />
            <p className="quote">
              With over 12,000 farms already using the system with more than
              7,000,000 ha of paddock data processed across Australia, UK,
              Africa, the Middle East, USA, and Canada.
            </p>
            <img
              src={`${PearceyFundationSvg}`}
              alt="pearcey img"
              className="image"
            />
          </div>
        </section>
        <section className="management-section">
          <section className="management-wrapper">
            <div className="left">
              <small className="preamble">datafarming advanced</small>
              <h3 className="title">Farm Management Made Easy</h3>
              <p className="paragraph">
                When you subscribe to advance, you can select multiple fields
                within your farm. When you select layers and calculations, it
                seamlessly updates fields selected on our mapping tool. It is
                that simple to view your crop status.
              </p>
            </div>
            <img
              src={`${FarmManagementImg}`}
              alt="farm-management img"
              className="image"
            />
          </section>
        </section>
        <section className="ventures-section">
          <div className="ventures-wrapper">
            <img src={`${SaveEarthImg}`} alt="venture img" />
            <h3 className="description">
              Datafarming is powering the world&lsquo;s most pioneering
              agricultural sectors and departments.
            </h3>
          </div>
        </section>
        <section className="pricing-section">
          <div className={`pricing-item ${!isPremium ? "-premium" : ""}`}>
            <h6 className="tier-level">Basic Account</h6>
            <p className="tier-description">Perfect for starters.</p>
            <div className="price-wrapper">
              <h3 className="price">Free</h3>
              <p className="append">forever, don&lsquo;t worry</p>
            </div>
            <button
              className="button"
              type="button"
              disabled={!isPremium}
              onClick={() => history.push("/settings#subscription-status")}
            >
              {isPremium ? "Switch to Basic" : "Current Plan"}
            </button>
            <ul className="feature-list">
              <li className="list-item">
                <CheckSquare
                  size={24}
                  color={!isPremium ? "#09BA51" : "#bcb9c2"}
                  weight="duotone"
                />
                <p className="item">Create farm map with field areas</p>
              </li>
              <li className="list-item">
                <CheckSquare
                  size={24}
                  color={!isPremium ? "#09BA51" : "#bcb9c2"}
                  weight="duotone"
                />
                <p className="item">Share farms</p>
              </li>
              <li className="list-item">
                <CheckSquare
                  size={24}
                  color={!isPremium ? "#09BA51" : "#bcb9c2"}
                  weight="duotone"
                />
                <p className="item">Calculate distance and area</p>
              </li>
              <li className="list-item">
                <CheckSquare
                  size={24}
                  color={!isPremium ? "#09BA51" : "#bcb9c2"}
                  weight="duotone"
                />
                <p className="item">
                  NDVI Imagery on single field or region wide
                </p>
              </li>
              <li className="list-item">
                <CheckSquare
                  size={24}
                  color={!isPremium ? "#09BA51" : "#bcb9c2"}
                  weight="duotone"
                />
                <p className="item">GPS enabled for field</p>
              </li>
            </ul>
          </div>
          <div className={`pricing-item ${isPremium ? "-premium" : ""}`}>
            <h6 className="tier-level">Advanced Account</h6>
            <p className="tier-description">For users who want to do more.</p>
            <div className="price-wrapper">
              {isFetchingPricingDetails && <ContentLoader />}
              {!isFetchingPricingDetails && (
                <>{isValidDiscount ? renderDiscountedPrice() : renderPrice()}</>
              )}
            </div>
            <button
              className="button"
              type="button"
              onClick={() => setToggleModal(true)}
              disabled={isPremium}
            >
              {isPremium ? "Current Plan" : "Subscribe to Advanced"}
            </button>
            <ul className="feature-list">
              <li className="list-item">
                <CheckSquare
                  size={24}
                  color={isPremium ? "#09BA51" : "#bcb9c2"}
                  weight="duotone"
                />
                <p className="item">All from Basic Features</p>
              </li>
              <li className="list-item">
                <CheckSquare
                  size={24}
                  color={isPremium ? "#09BA51" : "#bcb9c2"}
                  weight="duotone"
                />
                <p className="item">NDRE Imagery Calculation</p>
              </li>
              <li className="list-item">
                <CheckSquare
                  size={24}
                  color={isPremium ? "#09BA51" : "#bcb9c2"}
                  weight="duotone"
                />
                <p className="item">Thermal Imagery Calculation</p>
              </li>
              <li className="list-item">
                <CheckSquare
                  size={24}
                  color={isPremium ? "#09BA51" : "#bcb9c2"}
                  weight="duotone"
                />
                <p className="item">PCD Imagery Calculation</p>
              </li>
              <li className="list-item">
                <CheckSquare
                  size={24}
                  color={isPremium ? "#09BA51" : "#bcb9c2"}
                  weight="duotone"
                />
                <p className="item">Multi-field Imagery Viewing</p>
              </li>
            </ul>
          </div>
        </section>
        <div className="footer-container">
          <div className="footer-wrapper">
            <p className="text">
              © 2022 Datafarming Pty Ltd - Australia. All rights reserved.
            </p>
          </div>
        </div>
      </div>
      <CustomModal
        isOpen={toggleModal}
        onRequestClose={() => setToggleModal(false)}
        onClose={() => setToggleModal(false)}
        size="full"
      >
        {isPremium ? <SubscriptionSuccess /> : <SubscriptionForm />}
      </CustomModal>
    </>
  );
};

export default PremiumContainer;
